import React, { useState, useEffect } from 'react';
import { Trash2, Loader2, FileText, Mail, Phone, ExternalLink, Eye, Check, XCircle, Filter } from 'lucide-react';
import './AdminApplications.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchApplications();
  }, [statusFilter]);

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const url = statusFilter !== 'all' 
        ? `${API_URL}/api/admin/applications?status=${statusFilter}`
        : `${API_URL}/api/admin/applications`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApplications(data);
      }
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (applicationId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_URL}/api/admin/applications/${applicationId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchApplications();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const deleteApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/applications/${applicationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchApplications();
      }
    } catch (error) {
      console.error('Failed to delete application:', error);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      new: { bg: '#dbeafe', color: '#1d4ed8' },
      reviewed: { bg: '#fef3c7', color: '#d97706' },
      shortlisted: { bg: '#dcfce7', color: '#166534' },
      rejected: { bg: '#fee2e2', color: '#dc2626' }
    };
    return colors[status] || colors.new;
  };

  return (
    <div className="admin-applications" data-testid="admin-applications">
      <div className="page-header">
        <div>
          <h1>Job Applications</h1>
          <p>Review candidate applications</p>
        </div>
        <div className="filter-group">
          <Filter size={18} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="shortlisted">Shortlisted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <Loader2 className="spin" size={32} />
          <span>Loading applications...</span>
        </div>
      ) : applications.length === 0 ? (
        <div className="empty-container">
          <FileText size={48} />
          <h3>No applications yet</h3>
          <p>Job applications will appear here when candidates apply.</p>
        </div>
      ) : (
        <div className="applications-list">
          {applications.map((app) => {
            const statusStyle = getStatusColor(app.status);
            return (
              <div key={app.id} className="application-card" data-testid={`application-card-${app.id}`}>
                <div className="app-header">
                  <div className="app-job">
                    <span className="job-badge">{app.job_title}</span>
                    <span className="app-date">{formatDate(app.created_at)}</span>
                  </div>
                  <span className="status-badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                    {app.status}
                  </span>
                </div>

                <div className="applicant-info">
                  <h3 className="applicant-name">{app.applicant_name}</h3>
                  <div className="contact-row">
                    <a href={`mailto:${app.email}`} className="contact-link">
                      <Mail size={14} /> {app.email}
                    </a>
                    <a href={`tel:${app.phone}`} className="contact-link">
                      <Phone size={14} /> {app.phone}
                    </a>
                  </div>
                </div>

                {app.cover_letter && (
                  <div className="cover-letter">
                    <strong>Cover Letter:</strong>
                    <p>{app.cover_letter.substring(0, 200)}{app.cover_letter.length > 200 ? '...' : ''}</p>
                  </div>
                )}

                <div className="app-actions">
                  <a 
                    href={app.resume_url.startsWith('http') ? app.resume_url : `${API_URL}${app.resume_url}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="resume-link"
                  >
                    <ExternalLink size={14} /> View Resume
                  </a>
                  <div className="status-actions">
                    <button 
                      className="status-btn reviewed" 
                      onClick={() => updateStatus(app.id, 'reviewed')}
                      title="Mark as Reviewed"
                    >
                      <Eye size={14} />
                    </button>
                    <button 
                      className="status-btn shortlist" 
                      onClick={() => updateStatus(app.id, 'shortlisted')}
                      title="Shortlist"
                    >
                      <Check size={14} />
                    </button>
                    <button 
                      className="status-btn reject" 
                      onClick={() => updateStatus(app.id, 'rejected')}
                      title="Reject"
                    >
                      <XCircle size={14} />
                    </button>
                    <button 
                      className="status-btn delete" 
                      onClick={() => deleteApplication(app.id)}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminApplications;
